import { Link } from 'react-router-dom'
import {
  isWithinScheduledHours,
  nextOpenDescription,
  SCHEDULE_LINES,
  LATE_REQUESTS_OPEN_SENTENCE,
} from '../utils/schedule.js'

/**
 * Shown to dancers when this room isn't accepting requests at all.
 *
 * Two modes:
 *   • "closed"     → venue itself is not open. "We're closed" + schedule.
 *   • "unclaimed"  → venue is open but no instructor has claimed this room.
 *                    "This room isn't running right now."
 *
 * Note: the practice-mode banner used to be handled here, but it now lives
 * inline in DancerView — Room 2 in practice mode shows the full request UI
 * with a banner at the top, so it's not "closed" in any meaningful sense.
 */
export default function ClosedScreen({ room = 'main', mode = 'closed' }) {
  const isUnclaimed = mode === 'unclaimed'
  const isLate = room === 'late'
  const withinHours = isWithinScheduledHours()

  const accentBg = isLate
    ? 'bg-peak-accent2/10 border-peak-accent2/40'
    : 'bg-peak-accent/10 border-peak-accent/40'

  return (
    <div className="min-h-full grid place-items-center px-6 pb-16">
      <div className="card max-w-sm w-full text-center space-y-4">
        <div className="text-5xl" aria-hidden>
          {isUnclaimed ? '💤' : '🌙'}
        </div>

        <h1 className="text-2xl font-bold tracking-tight">
          {isUnclaimed
            ? 'This room isn’t running right now'
            : withinHours
              ? 'Setting up the night…'
              : "We're closed"}
        </h1>

        {isUnclaimed ? (
          <UnclaimedBody isLate={isLate} />
        ) : withinHours ? (
          <p className="text-sm text-peak-muted">
            The instructor hasn't kicked off requests yet. Hang tight — refresh the page in a minute.
          </p>
        ) : (
          <ClosedBody />
        )}

        {/* For Room 2 specifically, show when line dance requests typically open */}
        {isLate && !isUnclaimed && (
          <div className={`text-xs px-3 py-2 rounded-lg border ${accentBg}`}>
            {LATE_REQUESTS_OPEN_SENTENCE}
          </div>
        )}

        <Link to="/instructor" className="block text-xs text-peak-muted pt-3">
          Instructor sign-in →
        </Link>
      </div>
    </div>
  )
}

function UnclaimedBody({ isLate }) {
  return (
    <p className="text-sm text-peak-muted">
      No instructor has claimed this room yet. Try the{' '}
      <strong className="text-white">{isLate ? 'Main Room' : 'Room 2'}</strong>{' '}
      tab above, or check back in a few minutes.
    </p>
  )
}

function ClosedBody() {
  const nextOpen = nextOpenDescription()
  return (
    <>
      <p className="text-sm text-peak-muted">
        Song requests are open during these hours:
      </p>
      <ul className="text-sm font-medium space-y-1">
        {SCHEDULE_LINES.map((line) => (
          <li key={line} className="text-white">{line}</li>
        ))}
      </ul>
      {nextOpen && (
        <p className="text-xs text-peak-accent font-semibold pt-2">{nextOpen}</p>
      )}
    </>
  )
}
